import React, { useState } from "react";
import {
  Avatar,
  Button,
  Card,
  TextField,
  Typography,
  Grid,
  MenuItem,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  updateUser,
  uploadUserImage,
  changePassword,
} from "../../redux/userSlice";
import { toast } from "react-toastify";

const UserEdit = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: user?.name || "",
    surname: user?.surname || "",
    identificationNumber: user?.identificationNumber || "",
    contact: {
      countryCode: user?.contact?.countryCode || "",
      phoneNumber: user?.contact?.phoneNumber || "",
    },
    address: {
      description: user?.address?.description || "",
      city: user?.address?.city || "",
      town: user?.address?.town || "",
      district: user?.address?.district || "",
      code: user?.address?.code || "",
    },
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes("contact.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        contact: { ...prev.contact, [key]: value },
      }));
    } else if (name.includes("address.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [key]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const resultAction = await dispatch(uploadUserImage(file));
      const data = resultAction.payload;

      if (data?.imageUrl) {
        const updatedFormData = { ...formData, profileImage: data.imageUrl };
        setFormData(updatedFormData);

        // Görsel backend'e kaydedilsin
        dispatch(updateUser({ profileImage: data.imageUrl })).then((res) => {
          if (res?.meta?.requestStatus === "fulfilled") {
            toast.success("Profil fotoğrafı güncellendi.");
          } else {
            toast.error(res?.error?.message || "Fotoğraf güncellenemedi.");
          }
        });
      } else {
        toast.error("Yükleme başarılı ama URL bilgisi dönmedi.");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Fotoğraf yüklenemedi. Lütfen tekrar deneyin.");
    }
  };

  const handleSave = () => {
    if (
      formData.newPassword ||
      formData.confirmPassword ||
      formData.oldPassword
    ) {
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error("Yeni şifreler eşleşmiyor!");
        return;
      }
      dispatch(
        changePassword({
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        })
      )
        .unwrap()
        .then((res) => {
          toast.success(res.message || "Şifre başarıyla değiştirildi.");
          setFormData((prev) => ({
            ...prev,
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          }));
        })
        .catch((err) => {
          toast.error(err.message || "Şifre değiştirilemedi.");
        });
    }

    const {
      name,
      surname,
      identificationNumber,
      contact,
      address,
      profileImage,
    } = formData;

    const filteredPayload = {
      ...(name && { name }),
      ...(surname && { surname }),
      ...(identificationNumber && { identificationNumber }),
      ...(profileImage && { profileImage }),
      contact: {
        ...(contact.countryCode && { countryCode: contact.countryCode }),
        ...(contact.phoneNumber && { phoneNumber: contact.phoneNumber }),
      },
      address: {
        ...(address.description && { description: address.description }),
        ...(address.city && { city: address.city }),
        ...(address.town && { town: address.town }),
        ...(address.district && { district: address.district }),
        ...(address.code && { code: address.code }),
      },
    };

    dispatch(updateUser(filteredPayload)).then((res) => {
      if (res?.meta?.requestStatus === "fulfilled") {
        toast.success("Profil başarıyla güncellendi.");
      } else {
        toast.error(res?.error?.message || "Profil güncellenemedi.");
      }
    });
  };

  return (
    <div className="flex justify-center min-h-screen p-24">
      <Card className="w-full h-full max-w-lg p-6 rounded-2xl shadow-lg bg-gray-400">
        <Typography variant="h5" className="mb-4 font-semibold text-gray-800">
          Profil Düzenleme
        </Typography>
        <div className="flex flex-col items-center mb-4">
          <Avatar src={formData.profileImage} className="w-24 h-24 mb-2" />
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            id="profile-image-upload"
            className="block text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          />
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Ad"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Soyad"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="T.C. Kimlik No"
              name="identificationNumber"
              value={formData.identificationNumber}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Ülke Kodu"
              name="contact.countryCode"
              value={formData.contact.countryCode}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="">Seçiniz</MenuItem>
              <MenuItem value="+90">+90 (Türkiye)</MenuItem>
              <MenuItem value="+1">+1 (ABD/Kanada)</MenuItem>
              <MenuItem value="+44">+44 (İngiltere)</MenuItem>
              <MenuItem value="+49">+49 (Almanya)</MenuItem>
              <MenuItem value="+33">+33 (Fransa)</MenuItem>
              <MenuItem value="+39">+39 (İtalya)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Telefon Numarası"
              name="contact.phoneNumber"
              value={formData.contact.phoneNumber}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Adres Açıklaması"
              name="address.description"
              value={formData.address.description}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Şehir"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="İlçe"
              name="address.town"
              value={formData.address.town}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Mahalle/Semt"
              name="address.district"
              value={formData.address.district}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Posta Kodu"
              name="address.code"
              value={formData.address.code}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Eski Şifre"
              name="oldPassword"
              type="password"
              value={formData.oldPassword}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Yeni Şifre"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Yeni Şifreyi Onayla"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          onClick={handleSave}
          fullWidth
          sx={{
            backgroundColor: "#024202",
            "&:hover": { backgroundColor: "#023202" },
            mt: 3,
          }}
        >
          Kaydet
        </Button>
      </Card>
    </div>
  );
};

export default UserEdit;
